import { useLayoutEffect, useRef, useState } from 'react';
import { getSupabase } from '@/lib/supabase';
import { checkAccount, passwordSignIn, passwordSignUp } from '@/lib/api';
import { OTPCodeInput } from '@/components/OTPCodeInput';
import { clearAuthSession, saveAuthSession, type AuthSession } from './authSessionStorage';

const OTP_CODE_LENGTH = 8;

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '';

export type { AuthSession };

/**
 * Stages of the sign-in flow:
 * - `email-otp` (default): email + "Send sign-in code" button → goes to `otp-verify`.
 *    A "Sign in with password instead" link switches to `email-password`.
 * - `otp-verify`: code input + Verify (existing OTP path).
 * - `email-password`: email + Continue → calls /api/auth/check, then routes to
 *    `password-signin` or `password-signup` based on account state.
 * - `password-signin`: password + Sign in. Has "Forgot your password?" (falls
 *    back to OTP) and "Use email code instead" (also OTP).
 * - `password-signup`: password + confirm-password + Sign up. Has
 *    "Use email code instead" only — there's nothing to recover yet.
 */
type Stage = 'email-otp' | 'otp-verify' | 'email-password' | 'password-signin' | 'password-signup';

export function LoginStep(props: {
  email: string;
  setEmail: (e: string) => void;
  session: AuthSession | null;
  setSession: (s: AuthSession | null) => void;
  /** Stepper-provided callback to advance once we have a session. */
  next?: () => void;
}) {
  const [stage, setStage] = useState<Stage>('email-otp');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState<string | null>(null);

  // Keep the latest `next` in a ref so the deferred call after setSession()
  // sees the post-commit closure (which has session !== null and therefore
  // passes the stepper's validate gate). useLayoutEffect updates the ref
  // synchronously after commit; useEffect is async and can lose the race
  // against the setTimeout(0) below, leaving the ref pointing at a stale
  // closure where validate() still sees session === null.
  const nextRef = useRef(props.next);
  useLayoutEffect(() => {
    nextRef.current = props.next;
  }, [props.next]);

  function landSession(s: AuthSession): void {
    saveAuthSession(s);
    props.setSession(s);
    setInfo(null);
    setTimeout(() => nextRef.current?.(), 0);
  }

  function resetPasswordFields(): void {
    setPassword('');
    setConfirmPassword('');
  }

  async function sendOtp(): Promise<void> {
    setBusy(true);
    setInfo(null);
    try {
      const r = await fetch(`${API_BASE}/api/auth/otp/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: props.email }),
      });
      if (!r.ok) {
        const body = (await r.json().catch(() => ({}))) as { error?: string };
        setInfo(`Couldn't send code${body.error ? `: ${body.error}` : ''}.`);
        return;
      }
      setStage('otp-verify');
      setInfo('Check your email & spam folder for a sign-in code (it can take 30 seconds).');
    } finally {
      setBusy(false);
    }
  }

  async function verifyOtp(tokenOverride?: string): Promise<void> {
    const token = (tokenOverride ?? code).replace(/\D/g, '').slice(0, OTP_CODE_LENGTH);
    if (token.length < OTP_CODE_LENGTH) return;
    setBusy(true);
    setInfo(null);
    try {
      const sb = getSupabase();
      const { data, error } = await sb.auth.verifyOtp({
        email: props.email,
        token,
        type: 'email',
      });
      if (error || !data.session || !data.user) {
        setInfo('That code is invalid or expired.');
        return;
      }
      landSession({
        email: data.user.email ?? props.email,
        authToken: data.session.access_token,
        userId: data.user.id,
        expiresAt: data.session.expires_at,
      });
    } finally {
      setBusy(false);
    }
  }

  async function continueFromEmailPassword(): Promise<void> {
    if (!props.email) return;
    setBusy(true);
    setInfo(null);
    try {
      const status = await checkAccount(props.email);
      setStage(status.exists && status.hasPassword ? 'password-signin' : 'password-signup');
    } catch {
      setInfo("Couldn't look up that email. Try again.");
    } finally {
      setBusy(false);
    }
  }

  async function doPasswordSignIn(): Promise<void> {
    setBusy(true);
    setInfo(null);
    try {
      const r = await passwordSignIn({ email: props.email, password });
      landSession({
        email: r.email,
        authToken: r.authToken,
        userId: r.userId,
        expiresAt: r.expiresAt,
      });
    } catch (e) {
      const msg = String((e as Error).message ?? e);
      setInfo(
        msg === 'password_invalid'
          ? 'Email or password is incorrect.'
          : "Couldn't sign in. Try again."
      );
    } finally {
      setBusy(false);
    }
  }

  async function doPasswordSignUp(): Promise<void> {
    if (password !== confirmPassword) {
      setInfo('Passwords do not match.');
      return;
    }
    setBusy(true);
    setInfo(null);
    try {
      const r = await passwordSignUp({ email: props.email, password });
      landSession({
        email: r.email,
        authToken: r.authToken,
        userId: r.userId,
        expiresAt: r.expiresAt,
      });
    } catch (e) {
      const msg = String((e as Error).message ?? e);
      if (msg === 'password_already_set') {
        setInfo('An account with this email already exists. Please sign in.');
        setStage('password-signin');
        resetPasswordFields();
        return;
      }
      setInfo("Couldn't create your account. Try again.");
    } finally {
      setBusy(false);
    }
  }

  async function forgotPassword(): Promise<void> {
    // Recovery via OTP: send a sign-in code, then drop into the OTP-verify
    // stage. The user can update their password later from their account.
    resetPasswordFields();
    await sendOtp();
  }

  function backToEmailOtp(): void {
    setStage('email-otp');
    setCode('');
    resetPasswordFields();
    setInfo(null);
  }

  function onEmailEdit(value: string): void {
    props.setEmail(value);
    // If they edit the email after we've already moved past the email entry
    // stage, restart from the email entry stage of the same auth mode they
    // were on (OTP vs password). Editing on the entry stages themselves is
    // a no-op besides updating the value.
    if (stage === 'otp-verify') {
      setStage('email-otp');
      setCode('');
    } else if (stage === 'password-signin' || stage === 'password-signup') {
      setStage('email-password');
      resetPasswordFields();
    }
    setInfo(null);
  }

  if (props.session) {
    return (
      <div className="grid gap-3">
        <p className="text-sm text-bone/70">
          Signed in as <strong>{props.session.email}</strong>.
        </p>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            className="text-left text-xs uppercase tracking-wider text-accent hover:underline"
            onClick={() => {
              clearAuthSession();
              props.setSession(null);
              backToEmailOtp();
            }}
          >
            Use a different email
          </button>
          <button
            type="button"
            className="btn-primary w-full sm:w-auto"
            onClick={() => props.next?.()}
            data-testid="login-continue-signed"
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  const showEmailField =
    stage === 'email-otp' ||
    stage === 'email-password' ||
    stage === 'password-signin' ||
    stage === 'password-signup';

  return (
    <div className="grid gap-3">
      {showEmailField && (
        <label className="block">
          <span className="mb-1 block text-xs uppercase tracking-wider text-bone/60">Email</span>
          <input
            type="email"
            required
            value={props.email}
            onChange={(e) => onEmailEdit(e.target.value)}
            className="input"
            autoComplete="email"
            disabled={busy}
            data-testid="login-email"
          />
        </label>
      )}

      {stage === 'email-otp' && (
        <button
          type="button"
          onClick={sendOtp}
          disabled={busy || !props.email}
          className="btn-primary w-full sm:w-auto disabled:opacity-50"
          data-testid="login-continue-otp"
        >
          {busy ? 'Sending…' : 'Continue'}
        </button>
      )}

      {stage === 'email-password' && (
        <>
          <button
            type="button"
            onClick={continueFromEmailPassword}
            disabled={busy || !props.email}
            className="btn-primary w-full sm:w-auto disabled:opacity-50"
            data-testid="login-continue"
          >
            {busy ? 'Checking…' : 'Continue'}
          </button>
          <p className="text-xs text-bone/50">
            <button
              type="button"
              onClick={backToEmailOtp}
              disabled={busy}
              className="text-accent hover:underline"
              data-testid="login-use-otp"
            >
              Use email code instead
            </button>
          </p>
        </>
      )}

      {stage === 'password-signin' && (
        <>
          <label className="block">
            <span className="mb-1 block text-xs uppercase tracking-wider text-bone/60">
              Password
            </span>
            <input
              type="password"
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
              autoComplete="current-password"
              autoFocus
              data-testid="login-password"
            />
          </label>
          <button
            type="button"
            onClick={doPasswordSignIn}
            disabled={busy || password.length < 8}
            className="btn-primary w-full sm:w-auto disabled:opacity-50"
            data-testid="login-signin"
          >
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
          <ul className="flex flex-wrap gap-x-6 gap-y-2 pt-1 text-xs text-bone/50">
            <li>
              <button
                type="button"
                onClick={forgotPassword}
                disabled={busy}
                className="-my-1 py-2 text-accent hover:underline"
                data-testid="login-forgot"
              >
                Forgot your password?
              </button>
            </li>
            <li>
              <button
                type="button"
                onClick={sendOtp}
                disabled={busy}
                className="-my-1 py-2 text-accent hover:underline"
              >
                Use email code instead
              </button>
            </li>
          </ul>
        </>
      )}

      {stage === 'password-signup' && (
        <>
          <label className="block">
            <span className="mb-1 block text-xs uppercase tracking-wider text-bone/60">
              Choose a password
            </span>
            <input
              type="password"
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
              autoComplete="new-password"
              placeholder="At least 8 characters"
              autoFocus
              data-testid="signup-password"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs uppercase tracking-wider text-bone/60">
              Confirm password
            </span>
            <input
              type="password"
              minLength={8}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="input"
              autoComplete="new-password"
              data-testid="signup-confirm-password"
            />
          </label>
          <button
            type="button"
            onClick={doPasswordSignUp}
            disabled={busy || password.length < 8 || confirmPassword.length < 8}
            className="btn-primary w-full sm:w-auto disabled:opacity-50"
            data-testid="login-signup"
          >
            {busy ? 'Creating account…' : 'Sign up'}
          </button>
          <p className="text-xs text-bone/50">
            <button
              type="button"
              onClick={sendOtp}
              disabled={busy}
              className="text-accent hover:underline"
            >
              Use email code instead
            </button>
          </p>
        </>
      )}

      {stage === 'otp-verify' && (
        <>
          <fieldset className="mx-auto grid w-full max-w-md gap-3" aria-labelledby="otp-legend">
            <legend
              id="otp-legend"
              className="flex w-full rounded-md border border-stone-500/70 bg-stone-300 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-stone-950 shadow-sm"
            >
              Sign-in code
            </legend>
            <OTPCodeInput
              length={OTP_CODE_LENGTH}
              value={code}
              onChange={(next) => setCode(next.replace(/\D/g, '').slice(0, OTP_CODE_LENGTH))}
              onComplete={(next) => void verifyOtp(next)}
              disabled={busy}
              ariaLabel="Sign-in code"
              testId="otp-input"
            />
          </fieldset>
          <button
            type="button"
            onClick={() => void verifyOtp()}
            disabled={busy || code.length < OTP_CODE_LENGTH}
            className="btn-primary mx-auto w-full max-w-md disabled:opacity-50"
          >
            {busy ? 'Verifying…' : 'Verify'}
          </button>
          <div className="pt-1 text-center text-xs text-bone/50">
            <p className="mb-2">Didn&apos;t get a code?</p>
            <ul className="mx-auto grid w-full max-w-3xl grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-3">
              <li className="text-center sm:text-left">
                <button
                  type="button"
                  onClick={sendOtp}
                  disabled={busy}
                  className="-my-1 py-2 text-accent hover:underline"
                >
                  Resend
                </button>
              </li>
              <li className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    setCode('');
                    setInfo(null);
                    setStage('email-password');
                  }}
                  disabled={busy}
                  className="-my-1 py-2 text-accent hover:underline"
                  data-testid="otp-use-password"
                >
                  Sign in with password
                </button>
              </li>
              <li className="text-center sm:text-right">
                <button
                  type="button"
                  onClick={backToEmailOtp}
                  disabled={busy}
                  className="-my-1 py-2 text-accent hover:underline"
                >
                  Use a different email
                </button>
              </li>
            </ul>
          </div>
        </>
      )}

      {info && (
        <p
          className={`text-xs text-bone/60 ${stage === 'otp-verify' ? 'text-center' : ''}`}
          data-testid="login-info"
        >
          {info}
        </p>
      )}
    </div>
  );
}
